import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  getSchedulingOverview,
  type SchedulingOverviewQueryParams,
  type SchedulingOverviewResult,
} from '@services/api/dashboard/scheduling.api'
import { useAppSelector } from '@store/hooks'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetchState,
} from './useDashboardPeriod'

export function useSchedulingOverview(
  range: RangeKey,
  date: string,
  operatoryCount: number,
  enabled = true,
  queued = false,
): DashboardPeriodFetchState<SchedulingOverviewResult> {
  const refreshKey = useAppSelector((state) => state.patient.refresh)
  const baseParams = useDashboardPeriodParams(range, date)
  // 0 means the caller is holding the request (list not settled yet). A settled
  // empty list must pass at least 1 so the fetch still runs.
  const operatoriesReady = operatoryCount > 0
  const queryParams = useMemo((): SchedulingOverviewQueryParams | null => {
    if (!baseParams || !operatoriesReady) return null
    return { ...baseParams, operatoryCount }
  }, [baseParams, operatoryCount, operatoriesReady])

  return useDashboardPeriodFetch(queryParams, getSchedulingOverview, {
    refreshKey,
    enabled: enabled && operatoriesReady,
    queued,
    cacheKey: 'getSchedulingOverview',
  })
}
